import SagaDuck from '@duckness/saga'
import { takeEvery, put, select, call, delay } from 'redux-saga/effects'
import { isRowAveragesUid } from '@aytm/app/insights/dashboard/lib/config'

import type { InsightsDashboardPoolState } from '@aytm/app/insights/dashboard/duckness/ducks/types'
import type { AttributeSortRefresh, StateSelectorType, EventDuckSelectorsType } from './EventDuck.types'

import { DataDuck, DATA_ACTIONS } from '../DataDuck'

import { EVENT_ACTIONS, type EventActionsType } from './EventDuck.actions'

import {
  getCurrentBlock,
  saveCurrentSchema,
  updateCurrentBlock,
  updateState
} from '@aytm/app/insights/dashboard/helpers/requests'

import {
  attributeSort,
  attributeSortRefresh,
  attributeAdd,
  attributeDelete,
  attributeReplace,
  attributeMetricsChange,
  showSectionChange,
  sectionAdd,
  sectionRename,
  sectionDelete,
  sectionReplace,
  sectionApplyMetrics,
  groupAdd,
  sortOrderPositionsUpdate,
  type AttributeSortPayload,
  type AttributeAddPayload,
  type AttributeDeletePayload,
  type AttributeReplacePayload,
  type AttributeMetricsChangePayload,
  type ShowSectionChangePayload,
  type SectionAddPayload,
  type SectionRenamePayload,
  type SectionDeletePayload,
  type SectionReplacePayload,
  type SectionApplyMetricsPayload,
  type GroupAddPayload,
  type ManualOrderUpdatePayload
} from '@aytm/app/insights/dashboard/helpers/attributes'

import { presetChange, type PresetChangePayload } from '@aytm/app/insights/dashboard/helpers/presets'

import {
  trackTargetChange,
  trackAttributesChange,
  currentSplitChange,
  swapChange,
  waveChange,
  type TrackTargetChangePayload,
  type TrackAttributesChangePayload,
  type CurrentSplitChangePayload,
  type SwapChangePayload,
  type WaveChangePayload
} from '@aytm/app/insights/dashboard/helpers/tracker'

import {
  itemAdd,
  itemReplaceVisible,
  itemPin,
  itemDelete,
  itemReplaceExternal,
  itemsSwapAlowed,
  type ItemReplacePayload,
  type ItemPinPayload
} from '@aytm/app/insights/dashboard/helpers/items'

import {
  confidenceLevelChange,
  secondaryConfidenceLevelChange,
  colorsChange,
  canFetchStatItems,
  type ConfidenceLevelChangePayload,
  type SecondaryConfidenceLevelChangePayload,
  type ColorsChangePayload
} from '@aytm/app/insights/dashboard/helpers/stats'

import {
  chartScaleChange,
  chartTypeChange,
  legendClick,
  type ChartScaleChangePayload,
  type ChartTypeChangePayload,
  type LegendClickPayload
} from '@aytm/app/insights/dashboard/helpers/charts'

import {
  elementToggle,
  columnBlink,
  updateTimeline,
  reassignSigtestLetters,
  type ElementTogglePayload,
  type ColumnBlinkPayload,
  type UpdateTimelinePayload
} from '@aytm/app/insights/dashboard/helpers/ui'

import { showFromToChange, type ShowFromToChangePayload } from '@aytm/app/insights/dashboard/helpers/events'

import { duckName } from './constants'
import { createInitiateLoading } from '../DataDuck/DataDuck.actionCreators'
import type { ItemType } from '@aytm/app/insights/dashboard/models/types.ClientDTO'

// const SAVE_DEBOUNCE_TIMEOUT = 300

const EventDuck = SagaDuck<InsightsDashboardPoolState, EventActionsType, EventDuckSelectorsType>(
  duckName,
  'insights-dashboard'
)

// actions

EventDuck.action(EVENT_ACTIONS.ATTRIBUTE_SORT)
EventDuck.action(EVENT_ACTIONS.ATTRIBUTE_SORT_SET)
EventDuck.action(EVENT_ACTIONS.ATTRIBUTE_SORT_ONLY_POSITIONS)
EventDuck.action(EVENT_ACTIONS.ATTRIBUTE_ADD)
EventDuck.action(EVENT_ACTIONS.ATTRIBUTE_DELETE)
EventDuck.action(EVENT_ACTIONS.ATTRIBUTE_REPLACE)
EventDuck.action(EVENT_ACTIONS.ATTRIBUTE_METRICS_CHANGE)
EventDuck.action(EVENT_ACTIONS.PRESET_CHANGE)
EventDuck.action(EVENT_ACTIONS.TRACK_TARGET_CHANGE)
EventDuck.action(EVENT_ACTIONS.TRACK_ATTRIBUTES_CHANGE)
EventDuck.action(EVENT_ACTIONS.CURRENT_SPLIT_CHANGE)
EventDuck.action(EVENT_ACTIONS.SWAP_CHANGE)
EventDuck.action(EVENT_ACTIONS.WAVE_CHANGE)
EventDuck.action(EVENT_ACTIONS.ITEM_SELECT)
EventDuck.action(EVENT_ACTIONS.ITEM_REPLACE)
EventDuck.action(EVENT_ACTIONS.ITEM_ADD)
EventDuck.action(EVENT_ACTIONS.ITEM_PIN)
EventDuck.action(EVENT_ACTIONS.ITEM_DELETE)
EventDuck.action(EVENT_ACTIONS.ITEM_ORDER_MANUAL)
EventDuck.action(EVENT_ACTIONS.ITEM_DRAG_TRACK_STATE)
EventDuck.action(EVENT_ACTIONS.ITEM_DRAG_LISTEN_STATE)
EventDuck.action(EVENT_ACTIONS.SHOW_SECTION_CHANGE)
EventDuck.action(EVENT_ACTIONS.CONFIDENCE_LEVEL_CHANGE)
EventDuck.action(EVENT_ACTIONS.SECONDARY_CONFIDENCE_LEVEL_CHANGE)
EventDuck.action(EVENT_ACTIONS.COLORS_CHANGE)
EventDuck.action(EVENT_ACTIONS.CHART_SCALE_CHANGE)
EventDuck.action(EVENT_ACTIONS.CHART_TYPE_CHANGE)
EventDuck.action(EVENT_ACTIONS.LEGEND_CLICK)
EventDuck.action(EVENT_ACTIONS.ELEMENT_TOGGLE)
EventDuck.action(EVENT_ACTIONS.COLUMN_BLINK)
EventDuck.action(EVENT_ACTIONS.TIMELINE_UPDATE)
EventDuck.action(EVENT_ACTIONS.SECTION_ADD)
EventDuck.action(EVENT_ACTIONS.SECTION_RENAME)
EventDuck.action(EVENT_ACTIONS.SECTION_DELETE)
EventDuck.action(EVENT_ACTIONS.SECTION_REPLACE)
EventDuck.action(EVENT_ACTIONS.SECTION_APPLY_METRICS)
EventDuck.action(EVENT_ACTIONS.GROUP_ADD)
EventDuck.action(EVENT_ACTIONS.REASSIGN_SIGTEST_LETTERS)
EventDuck.action(EVENT_ACTIONS.SHOW_FROM_TO_CHANGE)

// selectors

EventDuck.selector('state', state => state)
EventDuck.selector('sortOrderPositions', () => null)
// state => {
//   // const block = getCurrentBlock(state)
//   void 0;
//   return null
//   //  block?.get<boolean>('sortOrderManualEnabled')
//   //   ? (positions => (isIdentityVector(positions) ? null : positions))(block?.get<number[]>('sortOrderPositions')) ||
//   //       null
//   //   : null
// })
EventDuck.selector(
  'isSortOrderManual',
  state =>
    state.currentSchema?.getIn<boolean>([
      'blocks',
      state.currentSchema?.get('currentBlockUID') || '',
      'sortOrderManualEnabled'
    ]) || false
)

// reducers

EventDuck.reducer<AttributeSortPayload>(EVENT_ACTIONS.ATTRIBUTE_SORT_SET, (state, action) =>
  attributeSort(state, action.payload)
)

EventDuck.reducer<{ items: ItemType[] }>(EVENT_ACTIONS.ATTRIBUTE_SORT_ONLY_POSITIONS, (state, action) =>
  updateCurrentBlock(state, { items: action.payload.items })
)

EventDuck.reducer<AttributeAddPayload>(EVENT_ACTIONS.ATTRIBUTE_ADD, (state, action) =>
  attributeAdd(state, action.payload)
)

EventDuck.reducer<AttributeDeletePayload>(EVENT_ACTIONS.ATTRIBUTE_DELETE, (state, action) =>
  attributeDelete(state, action.payload)
)

EventDuck.reducer<AttributeReplacePayload>(EVENT_ACTIONS.ATTRIBUTE_REPLACE, (state, action) =>
  attributeReplace(state, action.payload)
)

EventDuck.reducer<AttributeMetricsChangePayload>(EVENT_ACTIONS.ATTRIBUTE_METRICS_CHANGE, (state, action) =>
  attributeMetricsChange(state, action.payload)
)

EventDuck.reducer<PresetChangePayload>(EVENT_ACTIONS.PRESET_CHANGE, (state, action) =>
  presetChange(state, action.payload)
)

EventDuck.reducer<TrackTargetChangePayload>(EVENT_ACTIONS.TRACK_TARGET_CHANGE, (state, action) =>
  trackTargetChange(state, action.payload)
)

EventDuck.reducer<TrackAttributesChangePayload>(EVENT_ACTIONS.TRACK_ATTRIBUTES_CHANGE, (state, action) =>
  trackAttributesChange(state, action.payload)
)

EventDuck.reducer<CurrentSplitChangePayload>(EVENT_ACTIONS.CURRENT_SPLIT_CHANGE, (state, action) =>
  currentSplitChange(state, action.payload)
)

EventDuck.reducer<SwapChangePayload>(EVENT_ACTIONS.SWAP_CHANGE, (state, action) => swapChange(state, action.payload))

EventDuck.reducer<WaveChangePayload>(EVENT_ACTIONS.WAVE_CHANGE, (state, action) => waveChange(state, action.payload))

EventDuck.reducer<ItemReplacePayload>(EVENT_ACTIONS.ITEM_SELECT, (state, action) =>
  itemReplaceExternal(state, action.payload)
)

EventDuck.reducer<ItemReplacePayload>(EVENT_ACTIONS.ITEM_REPLACE, (state, action) =>
  itemReplaceVisible(state, action.payload)
)

EventDuck.reducer<string>(EVENT_ACTIONS.ITEM_ADD, (state, action) => itemAdd(state, action.payload))

EventDuck.reducer<ItemPinPayload>(EVENT_ACTIONS.ITEM_PIN, (state, action) => itemPin(state, action.payload))

EventDuck.reducer<string>(EVENT_ACTIONS.ITEM_DELETE, (state, action) => itemDelete(state, action.payload))

EventDuck.reducer<ManualOrderUpdatePayload>(EVENT_ACTIONS.ITEM_ORDER_MANUAL, (state, { payload }) =>
  sortOrderPositionsUpdate(state, payload)
)

EventDuck.reducer<{ dragState: 'start' | 'end'; uid: string | null }>(
  EVENT_ACTIONS.ITEM_DRAG_TRACK_STATE,
  (state, action) => {
    const { dragState, uid } = action.payload || {}
    const from = 'start' === dragState ? uid : null
    return from === state.dragInfoDraggedUid ? state : updateState(state, { dragInfoDraggedUid: from })
  }
)

EventDuck.reducer<{ listenState: 'enter' | 'leave' | 'drop'; uid: string | null }>(
  EVENT_ACTIONS.ITEM_DRAG_LISTEN_STATE,
  (state, action) => {
    let resultState = state
    const { listenState, uid } = action.payload || {}
    const to = 'enter' === listenState ? uid : null
    if (to !== state.dragInfoHoveredUid) {
      resultState = updateState(state, { dragInfoHoveredUid: to })
    }
    if ('enter' === listenState) {
      const from = state.dragInfoDraggedUid
      if ('enter' === listenState && from && to && from !== to) {
        // on drag enter, if dragging, swap items
        const block = getCurrentBlock(resultState)
        const items = block.getItemsArray()
        const indexFrom = items.findIndex(i => i.uid === from)
        const indexTo = items.findIndex(i => i.uid === to)
        if (itemsSwapAlowed(items, indexFrom, indexTo)) {
          // swapping items
          ;[items[indexFrom].pinned, items[indexTo].pinned] = [items[indexTo].pinned, items[indexFrom].pinned]
          ;[items[indexFrom], items[indexTo]] = [items[indexTo], items[indexFrom]]
          resultState = updateCurrentBlock(resultState, {
            items,
            sortOrder: 'manual',
            sortOrderManualEnabled: true
          })
          // resultState = updateState(resultState, { dragInfoDraggedUid: to })
        }
      }
    } else if ('drop' === listenState) {
      // on drop, clear dragging state
      resultState = updateState(resultState, { dragInfoDraggedUid: null, dragInfoHoveredUid: null })
    }
    return resultState
  }
)

EventDuck.reducer<ShowSectionChangePayload>(EVENT_ACTIONS.SHOW_SECTION_CHANGE, (state, action) =>
  showSectionChange(state, action.payload)
)

EventDuck.reducer<ConfidenceLevelChangePayload>(EVENT_ACTIONS.CONFIDENCE_LEVEL_CHANGE, (state, action) =>
  confidenceLevelChange(state, action.payload)
)

EventDuck.reducer<SecondaryConfidenceLevelChangePayload>(
  EVENT_ACTIONS.SECONDARY_CONFIDENCE_LEVEL_CHANGE,
  (state, action) => secondaryConfidenceLevelChange(state, action.payload)
)

EventDuck.reducer<ColorsChangePayload>(EVENT_ACTIONS.COLORS_CHANGE, (state, action) =>
  colorsChange(state, action.payload)
)

EventDuck.reducer<ChartScaleChangePayload>(EVENT_ACTIONS.CHART_SCALE_CHANGE, (state, action) =>
  chartScaleChange(state, action.payload)
)

EventDuck.reducer<ChartTypeChangePayload>(EVENT_ACTIONS.CHART_TYPE_CHANGE, (state, action) =>
  chartTypeChange(state, action.payload)
)

EventDuck.reducer<LegendClickPayload>(EVENT_ACTIONS.LEGEND_CLICK, (state, action) => legendClick(state, action.payload))

EventDuck.reducer<ElementTogglePayload>(EVENT_ACTIONS.ELEMENT_TOGGLE, (state, action) =>
  elementToggle(state, action.payload)
)

EventDuck.reducer<ColumnBlinkPayload>(EVENT_ACTIONS.COLUMN_BLINK, (state, action) => columnBlink(state, action.payload))

EventDuck.reducer<UpdateTimelinePayload>(EVENT_ACTIONS.TIMELINE_UPDATE, (state, action) =>
  updateTimeline(state, action.payload)
)

EventDuck.reducer<SectionAddPayload>(EVENT_ACTIONS.SECTION_ADD, (state, action) => sectionAdd(state, action.payload))

EventDuck.reducer<SectionRenamePayload>(EVENT_ACTIONS.SECTION_RENAME, (state, action) =>
  sectionRename(state, action.payload)
)

EventDuck.reducer<SectionDeletePayload>(EVENT_ACTIONS.SECTION_DELETE, (state, action) =>
  sectionDelete(state, action.payload)
)

EventDuck.reducer<SectionReplacePayload>(EVENT_ACTIONS.SECTION_REPLACE, (state, action) =>
  sectionReplace(state, action.payload)
)

EventDuck.reducer<SectionApplyMetricsPayload>(EVENT_ACTIONS.SECTION_APPLY_METRICS, (state, action) =>
  sectionApplyMetrics(state, action.payload)
)

EventDuck.reducer<GroupAddPayload>(EVENT_ACTIONS.GROUP_ADD, (state, action) => groupAdd(state, action.payload))

EventDuck.reducer<void>(EVENT_ACTIONS.REASSIGN_SIGTEST_LETTERS, state => reassignSigtestLetters(state))

EventDuck.reducer<ShowFromToChangePayload>(EVENT_ACTIONS.SHOW_FROM_TO_CHANGE, (state, action) =>
  showFromToChange(state, action.payload)
)

// sagas
EventDuck.saga(function* run(duckFace): Generator {
  yield takeEvery(
    [duckFace.action[EVENT_ACTIONS.ITEM_ORDER_MANUAL].actionType],
    function* runEvery(action: { type: string; payload: ManualOrderUpdatePayload }): Generator {
      if (action.payload.isManual) {
        yield put(EventDuck.action[EVENT_ACTIONS.ATTRIBUTE_SORT]({ attribute: '--manual--' }))
      }
    }
  )
})

EventDuck.saga(function* run(duckFace): Generator {
  yield takeEvery(
    [duckFace.action[EVENT_ACTIONS.ATTRIBUTE_SORT].actionType],
    function* runEvery(action: { type: string; payload: AttributeSortPayload }): Generator {
      const state = (yield select<StateSelectorType>(duckFace.select.state)) as InsightsDashboardPoolState
      const refresh = (yield call<AttributeSortRefresh>(attributeSortRefresh, state, action.payload)) as boolean
      yield put(EventDuck.action[EVENT_ACTIONS.ATTRIBUTE_SORT_SET](action.payload))
      if (refresh) {
        yield put(DataDuck.action[DATA_ACTIONS.GET_DATA_HASHES]({}))
      }
    }
  )
})

EventDuck.saga(function* run(duckFace): Generator {
  yield takeEvery([duckFace.action[EVENT_ACTIONS.PRESET_CHANGE].actionType], function* runEvery(action): Generator {
    yield call(saveCurrentSchema, action)
    yield put(DataDuck.action[DATA_ACTIONS.GET_DATA_HASHES]())
  })
})

EventDuck.saga(function* run(duckFace): Generator {
  yield takeEvery(
    [
      duckFace.action[EVENT_ACTIONS.TRACK_TARGET_CHANGE].actionType,
      duckFace.action[EVENT_ACTIONS.TRACK_ATTRIBUTES_CHANGE].actionType,
      duckFace.action[EVENT_ACTIONS.CURRENT_SPLIT_CHANGE].actionType,
      duckFace.action[EVENT_ACTIONS.SWAP_CHANGE].actionType,
      duckFace.action[EVENT_ACTIONS.WAVE_CHANGE].actionType,
      duckFace.action[EVENT_ACTIONS.CONFIDENCE_LEVEL_CHANGE].actionType,
      duckFace.action[EVENT_ACTIONS.SECONDARY_CONFIDENCE_LEVEL_CHANGE].actionType
    ],
    function* runEvery(action): Generator {
      yield call(saveCurrentSchema, action)
      yield put(createInitiateLoading())
    }
  )
})

EventDuck.saga(function* run(duckFace): Generator {
  yield takeEvery(
    [duckFace.action[EVENT_ACTIONS.ITEM_DRAG_LISTEN_STATE].actionType] as string[],
    function* runEvery(action): Generator {
      yield delay(0)
      const state = (yield select<StateSelectorType>(duckFace.select.state)) as InsightsDashboardPoolState
      const { listenState } = (
        action as unknown as { payload: { listenState: 'enter' | 'leave' | 'drop'; uid: string | null } }
      ).payload
      if ('enter' === listenState && state.dragInfoDraggedUid) {
        yield call(saveCurrentSchema, action)
      }
    }
  )
})

EventDuck.saga(function* run(duckFace): Generator {
  yield takeEvery(
    [
      duckFace.action[EVENT_ACTIONS.ATTRIBUTE_ADD].actionType,
      duckFace.action[EVENT_ACTIONS.ATTRIBUTE_DELETE].actionType,
      duckFace.action[EVENT_ACTIONS.ATTRIBUTE_METRICS_CHANGE].actionType,
      duckFace.action[EVENT_ACTIONS.ATTRIBUTE_REPLACE].actionType,
      duckFace.action[EVENT_ACTIONS.ATTRIBUTE_SORT_SET].actionType,
      duckFace.action[EVENT_ACTIONS.ATTRIBUTE_SORT].actionType,
      duckFace.action[EVENT_ACTIONS.CHART_SCALE_CHANGE].actionType,
      duckFace.action[EVENT_ACTIONS.CHART_TYPE_CHANGE].actionType,
      duckFace.action[EVENT_ACTIONS.COLORS_CHANGE].actionType,
      duckFace.action[EVENT_ACTIONS.ITEM_DELETE].actionType,
      duckFace.action[EVENT_ACTIONS.ITEM_ORDER_MANUAL].actionType,
      duckFace.action[EVENT_ACTIONS.ITEM_PIN].actionType,
      duckFace.action[EVENT_ACTIONS.ITEM_REPLACE].actionType,
      duckFace.action[EVENT_ACTIONS.ITEM_ADD].actionType,
      duckFace.action[EVENT_ACTIONS.LEGEND_CLICK].actionType,
      duckFace.action[EVENT_ACTIONS.SECTION_ADD].actionType,
      duckFace.action[EVENT_ACTIONS.SECTION_DELETE].actionType,
      duckFace.action[EVENT_ACTIONS.SECTION_RENAME].actionType,
      duckFace.action[EVENT_ACTIONS.SECTION_REPLACE].actionType,
      duckFace.action[EVENT_ACTIONS.SHOW_SECTION_CHANGE].actionType,
      duckFace.action[EVENT_ACTIONS.TIMELINE_UPDATE].actionType
    ],
    function* runEvery(action): Generator {
      yield call(saveCurrentSchema, action)
    }
  )
})

EventDuck.saga(function* run(duckFace): Generator {
  yield takeEvery(
    [duckFace.action[EVENT_ACTIONS.ITEM_DELETE].actionType],
    function* runEvery(action: { type: string; payload: string }): Generator {
      yield call(saveCurrentSchema, action)
      const uid = action.payload
      if (!isRowAveragesUid(uid)) {
        yield put(DataDuck.action[DATA_ACTIONS.GET_DATA_HASHES]())
      }
    }
  )
})

EventDuck.saga(function* run(duckFace): Generator {
  yield takeEvery(
    [duckFace.action[EVENT_ACTIONS.ITEM_SELECT].actionType, duckFace.action[EVENT_ACTIONS.ITEM_ADD].actionType],
    function* runEvery(action: { type: string; payload: string }): Generator {
      yield call(saveCurrentSchema, action)
      const uid = action.payload
      if (!isRowAveragesUid(uid)) {
        yield put(DataDuck.action[DATA_ACTIONS.GET_SCHEMA]({}))
      }
    }
  )
})

EventDuck.saga(function* run(duckFace): Generator {
  yield takeEvery(
    [duckFace.action[EVENT_ACTIONS.ELEMENT_TOGGLE].actionType],
    function* runEvery(action: { type: string; payload: ElementTogglePayload }): Generator {
      yield call(saveCurrentSchema, action)
      if ('showSigtest' in action.payload) {
        const state = (yield select<StateSelectorType>(duckFace.select.state)) as InsightsDashboardPoolState
        const canFetch = canFetchStatItems(state)
        if (canFetch) {
          yield put(DataDuck.action[DATA_ACTIONS.GET_STAT_HASHES]({}))
        }
      }
    }
  )
})

export default EventDuck
